import React, { useState } from 'react';
import { Instagram, MessageCircle, Plus, RefreshCcw, Smartphone } from 'lucide-react';
import ChatChannelBadge from '../Chat/ChannelBadge.tsx';
import type { ConnectionRow } from '../whatsapp-connect/WhatsAppConnections.tsx';
import type { InstagramConnection } from '../../services/instagram.ts';

export const channelsTheme = (dark: boolean) => ({
  canvas: dark ? 'bg-[#08111F] text-[#F8FAFC]' : 'bg-sminy-canvas text-sminy-text-primary',
  surface: dark ? 'bg-[#0F1D31] border-[#344054]' : 'bg-white border-sminy-border',
  subtle: dark ? 'bg-[#14243A]' : 'bg-sminy-blue-soft',
  body: dark ? 'text-[#D0D5DD]' : 'text-sminy-text-secondary',
  muted: dark ? 'text-[#98A2B3]' : 'text-sminy-muted',
  divider: dark ? 'border-[#344054]' : 'border-sminy-border',
  danger: dark ? 'text-[#FDA29B]' : 'text-[#B42318]',
});

export const ChannelButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & {
  dark: boolean; primary?: boolean; danger?: boolean;
}>(({ dark, primary, danger, className = '', children, ...props }, ref) => (
  <button type="button" ref={ref} {...props} className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
    primary ? 'border-transparent bg-sminy-primary text-white hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed'
      : danger ? dark ? 'border-transparent bg-[#3B1D24] text-[#FDA29B] hover:bg-[#3B1D24]/80' : 'border-transparent bg-[#FEF3F2] text-[#B42318] hover:bg-[#FEF3F2]/80'
      : `${channelsTheme(dark).surface} ${dark ? 'hover:bg-[#14243A]' : 'hover:bg-sminy-blue-soft'}`
  } ${className}`}>{children}</button>
));
ChannelButton.displayName = 'ChannelButton';

export interface ChannelRow {
  kind: 'official' | 'device' | 'instagram';
  provider?: ConnectionRow['provider'];
  name: string;
  profileName?: string;
  imageUrl?: string;
  identity: string;
  status: string;
  tone: 'success' | 'error' | 'neutral';
  reconnect: boolean;
  loading?: boolean;
}

export const whatsappChannelRows = (rows: ConnectionRow[]): ChannelRow[] => rows.map(row => ({
  ...row, identity: row.phone, loading: row.status === 'Verificando',
}));

export function instagramChannelRow(connection: InstagramConnection, loading = false, failed = false): ChannelRow {
  const labels: Record<string, string> = {
    connected: 'Conectado', provisioning: 'Ativação em andamento', pending_account: 'Selecione o perfil',
    action_required: 'Ação necessária', expired: 'Autorização expirada', error: 'Erro na conexão',
    disconnecting: 'Desconectando', disconnect_error: 'Falha ao desconectar', disconnected: 'Desconectado',
    oauth_pending: 'Autorização em andamento', oauth_error: 'Autorização não concluída',
    selection_required: 'Selecione o perfil', subscription_pending: 'Ativação em andamento',
    subscription_missing: 'Ação necessária', authorization_required: 'Renove a autorização',
    check_failed: 'Status indisponível', disconnect_pending: 'Desconexão em verificação',
    disconnect_failed: 'Falha ao desconectar',
  };
  const operational = connection.status === 'connected' && Boolean(connection.webhook_subscribed_at);
  const status = loading ? 'Verificando' : failed ? 'Status indisponível'
    : connection.status === 'connected' && !operational ? 'Ativação em andamento'
    : labels[connection.status] || 'Ação necessária';
  return {
    kind: 'instagram', name: 'Instagram · Direct', profileName: connection.name || undefined,
    imageUrl: connection.profile_picture_url || undefined,
    identity: connection.username ? `@${connection.username.replace(/^@/, '')}` : 'Perfil indisponível',
    status, loading, reconnect: !loading && !failed && ['expired', 'action_required', 'authorization_required', 'oauth_error'].includes(connection.status),
    tone: loading ? 'neutral' : failed ? 'error' : operational ? 'success'
      : ['error', 'expired', 'action_required', 'disconnect_error', 'disconnect_failed', 'check_failed', 'authorization_required', 'subscription_missing', 'oauth_error'].includes(connection.status) ? 'error' : 'neutral',
  };
}

export function ChannelBadge({ row, dark }: { row: ChannelRow; dark: boolean }) {
  return <span className={`inline-flex min-h-7 items-center rounded-full px-3 py-1 text-xs font-medium ${
    row.tone === 'success' ? dark ? 'bg-[#123A2A] text-[#38C982]' : 'bg-[#ECFDF3] text-[#067647]'
      : row.tone === 'error' ? dark ? 'bg-[#3B1D24] text-[#FDA29B]' : 'bg-[#FEF3F2] text-[#B42318]'
      : `${channelsTheme(dark).subtle} ${channelsTheme(dark).muted}`
  }`}>{row.status}</span>;
}

function ChannelAvatar({ row, dark }: { row: ChannelRow; dark: boolean }) {
  const [failed, setFailed] = useState<string>();
  const initials = row.kind === 'device' && row.profileName
    ? row.profileName.split(/\s+/).slice(0, 2).map(part => Array.from(part)[0]).join('').toLocaleUpperCase('pt-BR') : '';
  const Icon = row.kind === 'instagram' ? Instagram : MessageCircle;
  const channel = row.kind === 'instagram' ? 'instagram' : row.kind === 'official' ? 'official_whatsapp' : row.provider;
  return <span className="relative inline-flex h-11 w-11 shrink-0">
    <span className={`flex h-full w-full items-center justify-center overflow-hidden rounded-xl ${channelsTheme(dark).subtle}`}>
      {row.imageUrl && failed !== row.imageUrl
        ? <img src={row.imageUrl} alt={row.kind === 'instagram' ? `Foto do Instagram de ${row.profileName || row.identity}` : `Foto do WhatsApp de ${row.profileName || (row.kind === 'official' ? 'conta oficial' : 'dispositivo')}`} referrerPolicy="no-referrer" className="h-full w-full object-cover" onError={() => setFailed(row.imageUrl)} />
        : row.kind === 'official' ? <span aria-hidden="true" className="block h-full w-full"><ChatChannelBadge channel="official_whatsapp" variant="avatar" /></span>
        : initials ? <span aria-hidden="true" className="text-sm font-semibold text-sminy-primary">{initials}</span>
        : <Icon size={24} className="text-sminy-primary" aria-hidden="true" />}
    </span>
    <ChatChannelBadge channel={channel} />
  </span>;
}

export function ChannelsList({ rows, dark, busy, loading, onManage, onRefresh, onAdd, canAdd }: {
  rows: ChannelRow[]; dark: boolean; busy: boolean; loading: boolean; canAdd: boolean;
  onManage: (row: ChannelRow) => void; onRefresh: () => void; onAdd: () => void;
}) {
  const t = channelsTheme(dark);
  if (!rows.length && !loading) return <section className={`max-w-[760px] rounded-2xl border p-6 ${t.surface}`}>
    <h2 className="text-xl font-semibold">Nenhuma conexão</h2>
    <p className={`mt-3 text-sm ${t.body}`}>Conecte um canal para começar a receber mensagens.</p>
    <ChannelButton dark={dark} primary disabled={busy || !canAdd} onClick={onAdd} className="mt-6 w-full sm:w-auto">Adicionar conexão</ChannelButton>
  </section>;
  return <section aria-label="Conexões configuradas">
    <h2 className="text-base font-semibold">Suas conexões</h2>
    <p role="status" className={`mt-2 text-xs ${t.body}`}>{loading || rows.some(row => row.loading) ? 'Verificando conexões…'
      : rows.some(row => row.status === 'Status indisponível') ? 'Verifique as conexões abaixo'
      : `${rows.length} ${rows.length === 1 ? 'conexão configurada' : 'conexões configuradas'}`}</p>
    <div className={`mt-6 overflow-hidden rounded-2xl border ${t.surface}`}>
      <table className="block w-full text-left xl:table xl:table-fixed">
        <caption className="sr-only">Perfil, canal, número ou perfil, status e ações de cada conexão</caption>
        <thead className={`hidden text-[10px] xl:table-header-group ${t.subtle} ${t.muted}`}><tr>
          {['PERFIL / CANAL', 'NÚMERO OU PERFIL', 'STATUS', 'AÇÕES'].map((label, i) => <th key={label} scope="col" className={`px-5 py-4 font-medium ${i === 0 ? 'w-[37%]' : i === 1 ? 'w-[26%]' : 'w-[18.5%]'}`}>{label}</th>)}
        </tr></thead>
        <tbody className="block xl:table-row-group">{rows.map(row => <tr key={row.kind} data-connection={row.kind} className={`grid grid-cols-2 gap-x-3 border-b p-5 last:border-b-0 xl:table-row xl:p-0 ${t.divider}`}>
          <th scope="row" className="col-span-2 pb-4 font-medium xl:table-cell xl:px-5 xl:py-5">
            <div className="flex min-w-0 items-center gap-4"><ChannelAvatar row={row} dark={dark} /><div className="min-w-0">
              <p className="break-words text-sm">{row.profileName || row.name}</p>
              {row.profileName && <p className={`mt-2 text-[11px] font-normal ${t.body}`}>{row.name}</p>}
            </div></div>
          </th>
          <td className={`col-span-2 break-words pb-3 text-[13px] xl:table-cell xl:px-5 xl:py-5 ${t.body}`}>{row.identity}</td>
          <td className="self-center xl:table-cell xl:px-5 xl:py-5"><ChannelBadge row={row} dark={dark} /></td>
          <td className="xl:table-cell xl:px-5 xl:py-5"><ChannelButton dark={dark} primary={row.reconnect} disabled={busy || row.loading} onClick={() => onManage(row)} aria-label={`${row.reconnect ? 'Reconectar' : 'Gerenciar'} ${row.name}`} className="w-full xl:min-w-[120px]">{row.reconnect ? 'Reconectar' : 'Gerenciar'}</ChannelButton></td>
        </tr>)}</tbody>
      </table>
    </div>
    <p className={`mt-6 text-xs ${t.body}`}>Cada conexão tem seu próprio número ou perfil.</p>
    <ChannelButton dark={dark} disabled={busy || loading} onClick={onRefresh} className="mt-5 w-full sm:w-auto"><RefreshCcw size={14} aria-hidden="true" />Atualizar conexões</ChannelButton>
  </section>;
}

export function ChannelPicker({ dark, whatsappAvailable, instagramAvailable, busy, onSelect }: {
  dark: boolean; whatsappAvailable: boolean; instagramAvailable: boolean; busy: boolean;
  onSelect: (channel: 'whatsapp' | 'instagram') => void;
}) {
  const [selected, setSelected] = useState<'whatsapp' | 'instagram'>(instagramAvailable ? 'instagram' : 'whatsapp');
  const t = channelsTheme(dark);
  const options = [
    { id: 'whatsapp' as const, title: 'WhatsApp', description: 'Conecte pela Meta ou pelo seu celular.', available: whatsappAvailable, Icon: Smartphone },
    { id: 'instagram' as const, title: 'Instagram', description: 'Conecte sua conta profissional ao Direct.', available: instagramAvailable, Icon: Instagram },
  ];
  return <section className="w-full max-w-[760px]">
    <h2 className="text-xl font-semibold">Adicionar conexão</h2>
    <p className={`mt-4 text-[13px] ${t.body}`}>Escolha o canal que deseja conectar.</p>
    <fieldset className="mt-8 space-y-4"><legend className="sr-only">Canal</legend>{options.map(({ id, title, description, available, Icon }) => <label key={id} className={`flex min-h-24 items-start gap-4 rounded-xl border p-4 ${selected === id && available ? `border-sminy-primary ${dark ? 'bg-[#143A6B]' : 'bg-sminy-blue-light'}` : t.surface} ${available ? 'cursor-pointer' : 'opacity-60'}`}>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${t.subtle}`}><Icon size={24} className="text-sminy-primary" aria-hidden="true" /></span>
      <span className="min-w-0 flex-1"><span className="text-sm font-medium">{title}</span><span className={`mt-2 block text-xs leading-relaxed ${t.body}`}>{available ? description : 'Já configurado ou indisponível nesta clínica.'}</span></span>
      <input type="radio" name="channel" value={id} checked={selected === id} disabled={!available || busy} onChange={() => setSelected(id)} className="mt-1 h-[18px] w-[18px] shrink-0 accent-sminy-primary focus-visible:outline-offset-4" />
    </label>)}</fieldset>
    <p className={`mt-7 text-xs ${t.body}`}>{selected === 'instagram' ? 'Você será direcionado à Meta para autorizar o acesso.' : 'Na próxima etapa, escolha como conectar o WhatsApp.'}</p>
    <ChannelButton dark={dark} primary disabled={busy || !(selected === 'instagram' ? instagramAvailable : whatsappAvailable)} onClick={() => onSelect(selected)} className="mt-8 w-full sm:w-auto">{selected === 'instagram' ? 'Continuar com a Meta' : 'Continuar com WhatsApp'}</ChannelButton>
  </section>;
}

export const AddChannelButton = ({ dark, ...props }: { dark: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => <ChannelButton dark={dark} primary {...props}><Plus size={16} aria-hidden="true" />Adicionar conexão</ChannelButton>;
